import type { LeadStatus } from '@/lib/api/types';
import type { Permission } from '@/lib/rbac/permissions';

/**
 * Lead-type registry (plan §3.3 / spec §11). One engine serves all four lead types; they
 * differ by status set, columns, and which operations the backend exposes.
 */
export interface LeadTypeConfig {
  key: 'contact' | 'quotes' | 'applications' | 'newsletter' | 'seminar-applications';
  navKey: string;
  /**
   * English fallback label. Rendered copy is localized via `t(\`nav.${navKey}\`)` at the call
   * site (nav.contact/quotes/applications/newsletter already exist and match these strings).
   */
  label: string;
  /**
   * The permission that reveals this inbox, and the one that lets it be written
   * to. They are per-TYPE rather than a single `leads:read` for the whole group
   * because the seminar applications are not commercial leads: they are named
   * teenagers with a guardian's address attached, and since PHASE 01 of
   * PLAN_student-records-console they sit behind `students:read` /
   * `students:write`, which only `admin` holds (D-S3). Everything that gates a
   * lead surface — the nav, the page, the row actions, the detail dialog, the
   * dashboard card — reads these fields, so there is one place to change and
   * no surface can be forgotten.
   */
  readPermission: Permission;
  managePermission: Permission;
  /**
   * Gates the PDPL erasure. The commercial inboxes erase under `leads:manage`;
   * a seminar application is a student record, so deleting it needs
   * `students:delete`, held apart from `students:write` since round 10.
   */
  deletePermission: Permission;
  /** Statuses offered in the pipeline selector (spec §11.1). */
  statuses: LeadStatus[];
  hasDetail: boolean;
  updatable: boolean; // PATCH status/assignee/notes
  hasExport: boolean;
  hasErasure: boolean; // PDPL DELETE (gap 15)
}

const CONTACT_STATUSES: LeadStatus[] = [
  'new',
  'in_progress',
  'reviewing',
  'responded',
  'closed',
  'spam',
  'archived',
];

/**
 * The seminar application's pipeline (PHASE 04). It is the CONTACT set minus
 * `responded` — an application is reviewed and then shortlisted or rejected, and
 * "responded" says nothing about whether a place was offered. `hired` is absent
 * for the same reason it is absent from contact: it belongs to recruiting.
 */
const SEMINAR_APPLICATION_STATUSES: LeadStatus[] = [
  'new',
  'in_progress',
  'reviewing',
  'shortlisted',
  'rejected',
  'closed',
  'spam',
  'archived',
];

/**
 * Svet has three lead types: `contact` — the public contact form —
 * `seminar-applications`, the /apply/ form added by PHASE 04 of
 * PLAN_client-feedback-round-2, and `newsletter`, which PHASE 05 (CF-39) made a
 * real Svet surface: the home-page form feeds it, it now carries a Kit list, and
 * the client segments and exports by that list. Quotes and job applications are
 * inherited and unused (no public form feeds them). `nav.ts` auto-derives, so the
 * Leads group tracks this list, and the whole spam / validation / export /
 * erasure pipeline is reused unchanged for whatever is registered.
 */
export const LEAD_TYPES: LeadTypeConfig[] = [
  {
    key: 'contact',
    navKey: 'contact',
    label: 'Contact enquiries',
    readPermission: 'leads:read',
    managePermission: 'leads:manage',
    deletePermission: 'leads:manage',
    statuses: CONTACT_STATUSES,
    hasDetail: true,
    updatable: true,
    hasExport: true,
    hasErasure: true,
  },
  {
    key: 'seminar-applications',
    navKey: 'seminarApplications',
    label: 'Seminar applications',
    // The student gate, not the lead gate. /privacy/ publishes "access to
    // applications is limited to the two people who run Svet"; on `leads:read`
    // that was true only because two accounts happened to exist.
    readPermission: 'students:read',
    managePermission: 'students:write',
    deletePermission: 'students:delete',
    statuses: SEMINAR_APPLICATION_STATUSES,
    hasDetail: true,
    updatable: true,
    hasExport: true,
    // Erasure matters more here than anywhere else in the console: a guardian who
    // asks for their child's application to be removed is exercising a right, and
    // the DELETE removes both people's data in one action.
    hasErasure: true,
  },
  {
    key: 'newsletter',
    navKey: 'newsletter',
    label: 'Newsletter',
    readPermission: 'leads:read',
    managePermission: 'leads:manage',
    deletePermission: 'leads:manage',
    // A subscription has no pipeline: its lifecycle is driven by the subscriber
    // (confirm, unsubscribe), not by staff, so the shared LeadStatus set does not
    // apply. LeadsPage builds this filter from NEWSLETTER_STATUSES instead.
    statuses: [],
    // No detail route, no PATCH and no DELETE on the backend: a subscription is
    // not a workflow item, and erasure is the subscriber's own unsubscribe. The
    // list dialog still opens read-only, which is where `list` is shown.
    hasDetail: false,
    updatable: false,
    hasExport: true,
    hasErasure: false,
  },
];

export function getLeadType(key: string): LeadTypeConfig | undefined {
  return LEAD_TYPES.find((t) => t.key === key);
}

/**
 * B-R16-07: the lists whose sidebar entry carries an unopened count. The keys
 * are the API's (`GET /admin/leads/unread-counts`, `POST /admin/leads/{kind}/…/opened`).
 * "Opened" is team-wide: the first staff member to open an item clears it for
 * everyone.
 */
export const UNREAD_KINDS = ['contact', 'seminar-applications', 'newsletter', 'interest', 'calendly'] as const;
export type UnreadKind = (typeof UNREAD_KINDS)[number];

export function isUnreadKind(key: string): key is UnreadKind {
  return (UNREAD_KINDS as readonly string[]).includes(key);
}
